import React, { useState, useRef, useEffect, useCallback } from 'react';
import './App.css';
import GridHunterEngine from './engine/GridHunterEngine';
import { submitScore, awardXp, calculateSessionXp } from './services/GameServices';

function App() {
  const [phase, setPhase] = useState('title'); // 'title' | 'playing'
  const [ui, setUi] = useState({
    score: 0, combo: 0, wave: 1, lives: 5, accuracy: 100,
    gameOver: false, hits: 0, misses: 0, maxCombo: 0, waveTime: 20,
  });

  const canvasRef = useRef(null);
  const engineRef = useRef(null);

  const handleStart = useCallback(() => {
    setPhase('playing');
    setUi({
      score: 0, combo: 0, wave: 1, lives: 5, accuracy: 100,
      gameOver: false, hits: 0, misses: 0, maxCombo: 0, waveTime: 20,
    });
  }, []);

  const handleRestart = useCallback(() => {
    if (engineRef.current) {
      engineRef.current.stop();
      engineRef.current = null;
    }
    handleStart();
  }, [handleStart]);

  // Init engine when playing
  useEffect(() => {
    if (phase !== 'playing') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const engine = new GridHunterEngine(canvas, (state) => {
      setUi(prev => {
        if (prev.score === state.score && prev.combo === state.combo &&
            prev.wave === state.wave && prev.lives === state.lives &&
            prev.gameOver === state.gameOver) return prev;
        return state;
      });
    });

    const w = window.innerWidth;
    const h = window.innerHeight;
    engine.resize(w, h);
    engine.start();
    engineRef.current = engine;

    const handleResize = () => {
      engine.resize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      engine.stop();
      window.removeEventListener('resize', handleResize);
      engineRef.current = null;
    };
  }, [phase]);

  // Submit score when game over
  const submittedRef = useRef(false);
  useEffect(() => {
    if (ui.gameOver && !submittedRef.current) {
      submittedRef.current = true;
      const playerName = localStorage.getItem('misk_player_name') || 'Anonymous';
      const playerId = localStorage.getItem('misk_player_id') || 'anon_' + Date.now();
      if (!localStorage.getItem('misk_player_id')) localStorage.setItem('misk_player_id', playerId);
      const meta = { wave: ui.wave, accuracy: ui.accuracy, maxCombo: ui.maxCombo, hits: ui.hits, misses: ui.misses };
      submitScore('grid-hunter', playerName, ui.score, meta);
      const xp = calculateSessionXp('grid-hunter', { score: ui.score, wave: ui.wave, accuracy: ui.accuracy, maxCombo: ui.maxCombo });
      awardXp(playerId, xp, 'grid-hunter', { score: ui.score, ...meta });
    }
    if (!ui.gameOver) submittedRef.current = false;
  }, [ui.gameOver, ui.score, ui.wave, ui.accuracy, ui.maxCombo, ui.hits, ui.misses]);

  if (phase === 'title') {
    return (
      <div className="app-root">
        <div className="title-screen">
          <div className="title-grid-bg" />
          <span className="title-crosshair">{'\u2316'}</span>
          <div className="title-badge">MISK TECH UNIVERSITY</div>
          <h1 className="title-main">GRID</h1>
          <h2 className="title-sub">HUNTER</h2>
          <div className="title-tagline">Lock on. Take the shot.</div>
          <button className="title-start" onClick={handleStart}>
            ENTER THE GRID
          </button>
          <div className="title-footer">
            <span>Grid Hunter v1.0.0</span>
            <span>Miskulin Technologies</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-root">
      <canvas ref={canvasRef} />

      {/* HUD */}
      <div className="hud">
        <div className="hud-left">
          <span className="hud-score">{ui.score.toLocaleString()}</span>
          {ui.combo > 1 && <span className="hud-combo">x{ui.combo}</span>}
        </div>
        <div className="hud-center">
          <span className="hud-wave">WAVE {ui.wave}</span>
          <span className="hud-timer">{Math.ceil(ui.waveTime)}s</span>
        </div>
        <div className="hud-right">
          <span className="hud-accuracy">{ui.accuracy}%</span>
          <span style={{ color: ui.lives <= 2 ? '#ff0040' : '#ff6b00', fontWeight: 700 }}>
            {'\u2764\uFE0F'.repeat(Math.max(0, ui.lives))}
          </span>
        </div>
      </div>

      {/* Game Over */}
      {ui.gameOver && (
        <div className="gameover-overlay">
          <div className="gameover-card">
            <h1 className="gameover-title">TARGET LOST</h1>
            <div className="gameover-subtitle">MISSION TERMINATED</div>
            <div className="gameover-stats">
              <div className="gameover-stat">
                <div className="gameover-stat-value">{ui.score.toLocaleString()}</div>
                <div className="gameover-stat-label">FINAL SCORE</div>
              </div>
              <div className="gameover-stat">
                <div className="gameover-stat-value">{ui.wave}</div>
                <div className="gameover-stat-label">WAVES SURVIVED</div>
              </div>
              <div className="gameover-stat">
                <div className="gameover-stat-value">{ui.accuracy}%</div>
                <div className="gameover-stat-label">ACCURACY</div>
              </div>
              <div className="gameover-stat">
                <div className="gameover-stat-value">x{ui.maxCombo}</div>
                <div className="gameover-stat-label">BEST COMBO</div>
              </div>
              <div className="gameover-stat">
                <div className="gameover-stat-value">{ui.hits}</div>
                <div className="gameover-stat-label">HITS</div>
              </div>
              <div className="gameover-stat">
                <div className="gameover-stat-value">{ui.misses}</div>
                <div className="gameover-stat-label">MISSED</div>
              </div>
            </div>
            <button className="gameover-btn" onClick={handleRestart}>
              RE-ENGAGE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
